"use client";

import React from "react";
import styled, { css } from "styled-components";
import { TextButtonProps, TextButtonVariant } from "../utils/types";
import { buttonStyles } from "./styles";
import { COLORS, HOVER_COLORS, TEXT_BUTTON_CONFIG, TYPOGRAPHY } from "../utils/constants";
import { Icon, Text } from "@thenounproject/lingo-core";
import Link from "next/link";

type ButtonProps = {
  $variant: TextButtonVariant;
  $small: boolean;
};

const styling = css<ButtonProps>`
  ${buttonStyles}
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font-size: ${props =>
    props.$small ? TYPOGRAPHY.fontSize.small.desktop : TYPOGRAPHY.fontSize.body.desktop};
  font-weight: ${props =>
    props.$small ? TYPOGRAPHY.fontWeight.medium : TYPOGRAPHY.fontWeight.semibold};
  ${props => {
    const variant = TEXT_BUTTON_CONFIG.variants[props.$variant];
    return `
      color: ${variant.color};
      svg {
        fill: ${variant.color};
        stroke: ${variant.color};
      }

      &:hover:not(:disabled) {
        color: ${variant.hoverColor};
        svg {
          stroke: ${variant.hoverColor};
          fill: ${variant.hoverColor};
        }
      }
    `;
  }}

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    outline: none;

    svg {
      stroke: ${HOVER_COLORS.blue};
    }
  }
  &:focus {
    outline: none;
  }
`;

const BaseTextButton = styled.button<ButtonProps>`
  ${styling}
`;

const BaseTextLink = styled(Link)<ButtonProps>`
  ${styling}
`;

export const TextButton: React.FC<TextButtonProps> = ({
  href,
  target,
  rel,
  onClick,
  disabled,
  type = "button",
  variant = "black",
  small = false,
  children,
  className,
  icon,
  font,
  iconSize = 16,
  style,
  tabIndex = 0,
}) => {
  const commonProps = {
    $variant: variant,
    $small: small,
    onClick: disabled ? undefined : onClick,
    disabled,
    className,
    style,
    tabIndex: disabled ? -1 : tabIndex,
  };

  if (href) {
    return (
      <BaseTextLink
        {...commonProps}
        href={href}
        target={target}
        rel={target === "_blank" && !rel ? "noopener noreferrer" : rel}>
        {children && (
          <Text as="span" font={font ? font : "marketing.semiBold"} color={COLORS[variant]}>
            {children}
          </Text>
        )}
        {icon && (
          <Icon fill={COLORS[variant]} iconId={icon} size={iconSize} ml={children ? "8px" : "0"} />
        )}
      </BaseTextLink>
    );
  }

  return (
    <BaseTextButton {...commonProps} type={type}>
      {children}
      {icon && (
        <Icon fill={COLORS[variant]} iconId={icon} size={iconSize} ml={children ? "8px" : "0"} />
      )}
    </BaseTextButton>
  );
};
